// src/services/api.js
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'  
  : 'http://localhost:5001/api'; 

console.log('API Service: Using API URL:', API_URL);

// Create API client with additional retry logic
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Set longer timeout for Heroku's slow startups and long-running operations
  timeout: 300000, // 5 minutes - increased for large distribution/gather operations
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`);
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('API Request: Token added to Authorization header');
    } else {
      console.log('API Request: No token found in localStorage');
    }
    
    if (config.data) {
      console.log('API Request data:', config.data);
    }
    
    return config;
  },
  (error) => {
    console.error('API Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.statusText} from ${response.config.url}`);
    console.log('API Response data:', response.data);
    return response;
  },
  (error) => {
    console.error('API Response error:', {
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      console.log('API Response: 401 Unauthorized - redirecting to login');
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Handle server down errors (H10 on Heroku)
    if (!error.response || error.response.status === 503) {
      console.error('API Response: Server appears to be down or restarting');
      
      // Return a friendly error message
      const customError = new Error('Server temporarily unavailable. The application is recovering or restarting.');
      customError.isServerDown = true;
      customError.originalError = error;
      return Promise.reject(customError);
    }
    
    return Promise.reject(error);
  }
);

// Retry capability for API requests
const withRetry = async (fn, maxRetries = 2, delay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        console.log(`Retrying API request (attempt ${attempt} of ${maxRetries})...`);
      }
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on certain errors
      if (error.response && [401, 403, 404].includes(error.response.status)) {
        throw error;
      }
      
      // Only retry if server appears to be down or if network failed
      if (!error.response || error.response.status >= 500) {
        if (attempt < maxRetries) {
          const retryDelay = delay * Math.pow(2, attempt) + Math.random() * 1000;
          console.log(`Will retry in ${Math.round(retryDelay)}ms`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      } else {
        throw error;
      }
    }
  }
  
  // If we get here, all retries failed
  throw lastError;
};

// Trading Agent API functions
export const tradingAgentApi = {
  // Agent management
  async getAgents(tokenId = null) {
    console.log('tradingAgentApi.getAgents: Fetching agents', tokenId ? `for token: ${tokenId}` : '(all tokens)');
    return withRetry(async () => {
      try {
        const url = tokenId ? `/agents?tokenId=${encodeURIComponent(tokenId)}` : '/agents';
        const response = await api.get(url);
        console.log(`tradingAgentApi.getAgents: Received ${response.data.length} agents${tokenId ? ` for token ${tokenId}` : ''}`);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.getAgents ERROR:', error);
        
        // If server is down, return empty array instead of throwing
        if (error.isServerDown) {
          console.log('Server appears to be down, returning empty agents list');
          return [];
        }
        
        throw error;
      }
    });
  },
  
  async getAgentChat(agentId) {
  console.log('tradingAgentApi.getAgentChat: Fetching chat for agent:', agentId);
  return withRetry(async () => {
    try {
      const response = await api.get(`/agents/${agentId}/chat`);
      console.log(`tradingAgentApi.getAgentChat: Received ${response.data.length} messages`);
      
      // Cache the messages for offline use
      this.cacheAgentChat(agentId, response.data);
      
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.getAgentChat ERROR:', error);
      
      // If server is down, return cached messages if available
      if (error.isServerDown) {
        const cachedMessages = JSON.parse(localStorage.getItem(`agentChat-${agentId}`) || '[]');
        console.log('Server appears to be down, returning cached chat messages:', cachedMessages.length);
        return cachedMessages;
      }
      
      throw error;
    }
  });
},

cacheAgentStatus: (agentId, isActive) => {
  if (agentId) {
    localStorage.setItem(`agent_status_${agentId}`, JSON.stringify({ active: isActive }));
    console.log(`Cached agent ${agentId} status: active=${isActive}`);
  }
},


async addAgentChatMessage(agentId, role, content) {
  console.log('tradingAgentApi.addAgentChatMessage: Adding message for agent:', agentId);
  return withRetry(async () => {
    try {
      const response = await api.post(`/agents/${agentId}/chat`, { role, content });
      console.log('tradingAgentApi.addAgentChatMessage: Response:', response.data);
      
      // Update the cache with the new message
      const cachedMessages = JSON.parse(localStorage.getItem(`agentChat-${agentId}`) || '[]');
      cachedMessages.push(response.data);
      localStorage.setItem(`agentChat-${agentId}`, JSON.stringify(cachedMessages));
      
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.addAgentChatMessage ERROR:', error);
      
      // If server is down, store message locally
      if (error.isServerDown) {
        console.log('Server appears to be down, caching message locally');
        const cachedMessages = JSON.parse(localStorage.getItem(`agentChat-${agentId}`) || '[]');
        const newMessage = {
          id: Date.now(),
          agent_id: agentId,
          role,
          content,
          timestamp: new Date().toISOString()
        };
        cachedMessages.push(newMessage);
        localStorage.setItem(`agentChat-${agentId}`, JSON.stringify(cachedMessages));
        return newMessage;
      }
      
      throw error;
    }
  });
},

// Cache agent chat messages for offline use
cacheAgentChat(agentId, messages) {
  if (messages && messages.length > 0) {
    localStorage.setItem(`agentChat-${agentId}`, JSON.stringify(messages));
    console.log(`Cached ${messages.length} chat messages for agent ${agentId}`);
  }
},
  async createAgent(name, tokenId = null) {
    console.log('tradingAgentApi.createAgent: Creating agent with name:', name, 'tokenId:', tokenId);
    return withRetry(async () => {
      try {
        const requestData = { name };
        if (tokenId) {
          requestData.tokenId = tokenId;
        }
        const response = await api.post('/agents', requestData);
        console.log('tradingAgentApi.createAgent: Agent created successfully:', response.data);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.createAgent ERROR:', error);
        throw error;
      }
    });
  },
  
  async getAgentById(agentId) {
    console.log('tradingAgentApi.getAgentById: Fetching agent with ID:', agentId);
    return withRetry(async () => {
      try {
        const response = await api.get(`/agents/${agentId}`);
        console.log('tradingAgentApi.getAgentById: Agent details:', response.data);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.getAgentById ERROR:', error);
        
        // If server is down, return a default agent object
        if (error.isServerDown) {
          console.log('Server appears to be down, returning cached agent data if available');
          const cachedAgents = JSON.parse(localStorage.getItem('cachedAgents') || '[]');
          const cachedAgent = cachedAgents.find(a => a.id === parseInt(agentId));
          
          if (cachedAgent) {
            return {
              ...cachedAgent,
              status: 'offline',
              strategies: []
            };
          }
        }
        
        throw error;
      }
    });
  },
  
async deleteAgent(agentId, externalId) {
  console.log('tradingAgentApi.deleteAgent: Deleting agent with ID:', agentId, 'external_id:', externalId);
  return withRetry(async () => {
    try {
      // Pass the external_id as a query parameter to ensure it's available in the backend
      const response = await api.delete(`/agents/${agentId}/delete?external_id=${externalId}`);
      console.log('tradingAgentApi.deleteAgent: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.deleteAgent ERROR:', error);
      throw error;
    }
  });
},
  
  // Trading functions
  async parseInstruction(agentId, instruction, tokenAddress, walletAddresses) {
    console.log(agentId, 'ID', instruction, 'INSTRUCTION', tokenAddress, 'TOKEN ADDRESS', walletAddresses, 'WALLET ADDRESSES')
    console.log('tradingAgentApi.parseInstruction: Parsing instruction for agent:', agentId);
    return withRetry(async () => {
      try {
        const response = await api.post(`/agents/${agentId}/parse`, { 
          instruction,
          token_address: tokenAddress,
          wallet_addresses: walletAddresses
        });
        console.log('tradingAgentApi.parseInstruction: Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.parseInstruction ERROR:', error);
        throw error;
      }
    });
  },
  
async startStrategy(agentId, strategy) {
  console.log('tradingAgentApi.startStrategy: Starting strategy for agent:', agentId);
  return withRetry(async () => {
    try {
      const response = await api.post(`/agents/${agentId}/start`, { strategy });
      console.log('tradingAgentApi.startStrategy: Response:', response.data);
      
      // Cache the agent's active status
      this.cacheAgentStatus(agentId, true);
      
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.startStrategy ERROR:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  });
},

  
async stopStrategy(agentId, strategyId) {
  console.log('tradingAgentApi.stopStrategy: Stopping strategy:', { agentId, strategyId });
  return withRetry(async () => {
    try {
      const response = await api.post(`/agents/${agentId}/stop`, { strategyId });
      console.log('tradingAgentApi.stopStrategy: Response:', response.data);
      
      // Cache the agent's inactive status
      this.cacheAgentStatus(agentId, false);
      
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.stopStrategy ERROR:', error);
      
      // If we get a 200 OK status but with an error, consider it successful
      if (error.response && error.response.status === 200) {
        console.log('Received 200 OK status with error, treating as success');
        
        // Cache the agent's inactive status
        this.cacheAgentStatus(agentId, false);
        
        return { success: true, message: 'Strategy stopped successfully' };
      }
      
      throw error;
    }
  });
},
  
  async pauseStrategy(agentId, strategyId) {
    console.log('tradingAgentApi.pauseStrategy: Pausing strategy:', { agentId, strategyId });
    return withRetry(async () => {
      try {
        const response = await api.post(`/agents/${agentId}/pause`, { strategyId });
        console.log('tradingAgentApi.pauseStrategy: Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.pauseStrategy ERROR:', error);
        
        // Special handling for pause functionality
        // If we get a 200 OK status but with an error, consider it successful
        if (error.response && error.response.status === 200) {
          console.log('Received 200 OK status with error for pause, treating as success');
          return { success: true, paused: true, message: 'Strategy paused successfully' };
        }
        
        throw error;
      }
    });
  },
  
  async resumeStrategy(agentId, strategyId) {
    console.log('tradingAgentApi.resumeStrategy: Resuming strategy:', { agentId, strategyId });
    return withRetry(async () => {
      try {
        const response = await api.post(`/agents/${agentId}/resume`, { strategyId });
        console.log('tradingAgentApi.resumeStrategy: Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.resumeStrategy ERROR:', error);
        
        // If we get a 200 OK status but with an error, consider it successful
        if (error.response && error.response.status === 200) {
          console.log('Received 200 OK status with error for resume, treating as success');
          return { success: true, resumed: true, message: 'Strategy resumed successfully' };
        }
        
        throw error;
      }
    });
  },
  
  async getAgentStatus(agentId) {
    console.log('tradingAgentApi.getAgentStatus: Getting status for agent:', agentId);
    return withRetry(async () => {
      try {
        const response = await api.get(`/agents/${agentId}/status`);
        console.log('tradingAgentApi.getAgentStatus: Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.getAgentStatus ERROR:', error);

        // If server is down, return offline status
        if (error.isServerDown) {
          return { status: 'offline' };
        }

        throw error;
      }
    });
  },

  async modifyPreference(agentId, message) {
    console.log('tradingAgentApi.modifyPreference: Modifying preference for agent:', agentId);
    return withRetry(async () => {
      try {
        const response = await api.post(`/agents/${agentId}/modify_preference`, {
          message
        });
        console.log('tradingAgentApi.modifyPreference: Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.modifyPreference ERROR:', error);
        throw error;
      }
    });
  },
  
  async getAllAgentStatuses() {
    console.log('tradingAgentApi.getAllAgentStatuses: Getting all agent statuses');
    return withRetry(async () => {
      try {
        const response = await api.get(`/agents/status/all`);
        console.log('tradingAgentApi.getAllAgentStatuses: Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.getAllAgentStatuses ERROR:', error);
        
        // If server is down, return empty object
        if (error.isServerDown) {
          return {};
        }
        
        throw error;
      }
    });
  },
  
async getStrategyLogs(agentId, strategyId) {
  console.log('tradingAgentApi.getStrategyLogs: Getting logs for strategy:', { agentId, strategyId });
  return withRetry(async () => {
    try {
      // NOTE: Fixed URL format - removed "dashboard" from the path
      const response = await api.get(`/agents/${agentId}/strategies/${strategyId}/logs`);
      console.log(`tradingAgentApi.getStrategyLogs: Received ${response.data.length} logs`);
      
      // Cache logs for offline use
      this.cacheStrategyLogs(strategyId, response.data);
      
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.getStrategyLogs ERROR:', error);
      
      // If server is down, return cached logs
      if (error.isServerDown) {
        // Try to get cached logs from local storage
        const cachedLogs = JSON.parse(localStorage.getItem(`strategyLogs-${strategyId}`) || '[]');
        console.log('Server appears to be down, returning cached logs:', cachedLogs.length);
        return cachedLogs;
      }
      
      throw error;
    }
  }, 3, 2000); // More retries with longer delay for logs
},
  // Wallet functions

// Add this at the top of the tradingAgentApi object:
// Flag to track connection state and prevent reconnection loops
_isConnecting: false,
_lastConnectionTime: 0,
_connectionRequestCount: 0,
_requestCountResetTimer: null,

// Replace the connectWallet function with this improved version
async connectWallet(walletAddress) {
  // Prevent connection spam
  const now = Date.now();
  this._connectionRequestCount++;
  
  // Reset the counter after 10 seconds of inactivity
  clearTimeout(this._requestCountResetTimer);
  this._requestCountResetTimer = setTimeout(() => {
    this._connectionRequestCount = 0;
  }, 10000);
  
  // If we're getting too many connection requests in a short period, something is wrong
  if (this._connectionRequestCount > 5 && now - this._lastConnectionTime < 5000) {
    console.error('TOO MANY CONNECTION REQUESTS DETECTED - POSSIBLE INFINITE LOOP');
    console.trace('Connection request stack trace:');
    return {
      success: false,
      message: 'Connection rate limit exceeded - possible infinite loop',
      walletAddress
    };
  }
  
  // Check if already connecting
  if (this._isConnecting) {
    console.log('Connection already in progress, ignoring duplicate request');
    return {
      success: false,
      message: 'Connection already in progress',
      walletAddress
    };
  }
  
  // Check if already connected to this wallet
  const currentlyConnected = this.getConnectedWalletAddress();
  if (currentlyConnected === walletAddress) {
    console.log(`Already connected to wallet ${walletAddress}, skipping redundant connection`);
    return {
      success: true,
      message: 'Already connected to this wallet',
      walletAddress,
      isAlreadyConnected: true
    };
  }
  
  this._isConnecting = true;
  this._lastConnectionTime = now;
  
  console.log('tradingAgentApi.connectWallet: Connecting wallet:', walletAddress);
  
  return withRetry(async () => {
    try {
      // Store connected wallet in localStorage for persistence
      localStorage.setItem('connectedWalletAddress', walletAddress);
      
      const response = await api.post('/wallet/connect', { walletAddress });
      console.log('tradingAgentApi.connectWallet: Response:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.connectWallet ERROR:', error);
      
      // If server is down, save to localStorage anyway for offline use
      if (error.isServerDown) {
        localStorage.setItem('connectedWalletAddress', walletAddress);
        
        return { 
          success: true, 
          message: 'Wallet connected (offline mode)', 
          walletAddress 
        };
      }
      
      throw error;
    } finally {
      // Release the connection lock with a small delay to prevent immediate reconnection attempts
      setTimeout(() => {
        this._isConnecting = false;
      }, 2000);
    }
  });
},

async getWalletBalance(walletAddress) {
  // Don't even try to fetch if no wallet address is provided
  if (!walletAddress) {
    console.warn('getWalletBalance: No wallet address provided');
    return {
      solBalance: 0,
      tokens: []
    };
  }
  
  // Check if enough time has passed since the last request
  const now = Date.now();
  const lastCall = this._lastBalanceCall || 0;
  const callDelay = 3000; // 3 seconds minimum between calls
  
  if (now - lastCall < callDelay) {
    console.log(`Throttling wallet balance request - last call was ${now - lastCall}ms ago`);
    
    // Return cached balance if available
    const cachedBalance = JSON.parse(
      localStorage.getItem(`cachedWalletBalance-${walletAddress}`) || 'null'
    );
    
    if (cachedBalance) {
      return cachedBalance;
    }
  }
  
  // Update last call time
  this._lastBalanceCall = now;
  
  console.log('tradingAgentApi.getWalletBalance: Getting wallet balance for:', walletAddress);
  
  return withRetry(async () => {
    try {
      // Try the dedicated balance endpoint first
      try {
        const response = await api.get(`/wallet/${walletAddress}/balance`);
        console.log('tradingAgentApi.getWalletBalance: Response from balance endpoint:', response.data);
        
        // If we got a good response with solBalance, use that
        if (response.data && response.data.solBalance !== undefined) {
          return response.data;
        }
      } catch (balanceError) {
        console.log('Balance endpoint failed, falling back to wallet lookup endpoint');
      }
      
      // Fall back to standard wallet endpoint
      const response = await api.get(`/wallet/${walletAddress}`);
      console.log('tradingAgentApi.getWalletBalance: Raw response from fallback endpoint:', response.data);
      
      if (response.data) {
        // DIRECT APPROACH: Simply use the solBalance that's already in the response
        // Format the response to match the expected structure
        const formattedBalance = {
          solBalance: response.data.solBalance !== undefined ? 
                     Number(response.data.solBalance) : 0,
          tokens: []
        };
        
        console.log(`Using direct SOL balance from API: ${formattedBalance.solBalance}`);
        
        // Process tokens array
        if (response.data.tokens && Array.isArray(response.data.tokens)) {
          console.log(`Processing ${response.data.tokens.length} tokens`);
          
          response.data.tokens.forEach(token => {
            if (token.mint !== 'So11111111111111111111111111111111111111112' && token.tokenAmount) {
              try {
                const amount = parseFloat(token.tokenAmount.amount) / Math.pow(10, parseInt(token.tokenAmount.decimals || 0));
                // Try to get token symbol
                const symbol = token.symbol || token.mint.slice(0, 4) + '...';
                
                formattedBalance.tokens.push({
                  symbol: symbol,
                  balance: parseFloat(amount.toFixed(4)),
                  mint: token.mint
                });
              } catch (e) {
                console.error('Error parsing token amount:', e);
              }
            }
          });
        }
        
        console.log('Final formatted balance:', formattedBalance);
        
        // Cache wallet balance for offline use
        localStorage.setItem(`cachedWalletBalance-${walletAddress}`, JSON.stringify(formattedBalance));
        
        return formattedBalance;
      }
      
      // If we reach here, return a default empty balance
      console.log('No valid response data, returning empty balance');
      return {
        solBalance: 0,
        tokens: []
      };
    } catch (error) {
      console.error('tradingAgentApi.getWalletBalance ERROR:', error);
      
      // If server is down, return cached balance or default
      const cachedBalance = JSON.parse(
        localStorage.getItem(`cachedWalletBalance-${walletAddress}`) || 'null'
      );
      
      if (cachedBalance) {
        console.log('Returning cached balance:', cachedBalance);
        return cachedBalance;
      }
      
      // Return empty data
      console.log('No cached balance available, returning empty balance');
      return {
        solBalance: 0,
        tokens: []
      };
    }
  });
},

// Check if a wallet is already connected
isWalletConnected() {
  const connectedWalletAddress = localStorage.getItem('connectedWalletAddress');
  return !!connectedWalletAddress;
},

// Get the currently connected wallet address
getConnectedWalletAddress() {
  return localStorage.getItem('connectedWalletAddress');
},

disconnectWallet() {
  // Track the wallet we're disconnecting
  const walletBeingDisconnected = localStorage.getItem('connectedWalletAddress');
  
  // Remove from localStorage
  localStorage.removeItem('connectedWalletAddress');
  
  console.log(`Wallet disconnected: ${walletBeingDisconnected || 'none'}`);
  
  // Only make API call if actually connected
  if (walletBeingDisconnected) {
    // Fire and forget - don't wait for response
    api.post('/wallet/disconnect').catch(error => {
      console.error('Error in disconnect API call:', error);
    });
  }
  
  return { 
    success: true, 
    message: 'Wallet disconnected', 
    walletAddress: walletBeingDisconnected 
  };
},

  // Test API connection to external service
  async testExternalConnection() {
    console.log('tradingAgentApi.testExternalConnection: Testing connection to external API');
    return withRetry(async () => {
      try {
        const response = await api.get('/test-external-api');
        console.log('tradingAgentApi.testExternalConnection: Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('tradingAgentApi.testExternalConnection ERROR:', error);
        throw error;
      }
    });
  },
  
  // Cache agents data for offline use
  cacheAgents(agents) {
    if (agents && agents.length > 0) {
      localStorage.setItem('cachedAgents', JSON.stringify(agents));
      console.log(`Cached ${agents.length} agents for offline use`);
    }
  },
  
  // Cache strategy logs for offline use
  cacheStrategyLogs(strategyId, logs) {
    if (logs && logs.length > 0) {
      localStorage.setItem(`strategyLogs-${strategyId}`, JSON.stringify(logs));
      console.log(`Cached ${logs.length} logs for strategy ${strategyId}`);
    }
  },

  async getTerminalLogs(agentId, limit = 100) {
  console.log(`tradingAgentApi.getTerminalLogs: Fetching terminal logs for agent ${agentId}`);
  return withRetry(async () => {
    try {
      const response = await api.get(`/agents/${agentId}/terminal-logs?limit=${limit}`);
      console.log(`tradingAgentApi.getTerminalLogs: Received ${response.data.length} logs`);
      
      // Cache logs for offline use
      this.cacheTerminalLogs(agentId, response.data);
      
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.getTerminalLogs ERROR:', error);
      
      // If server is down, return cached logs
      if (error.isServerDown) {
        const cachedLogs = JSON.parse(localStorage.getItem(`terminalLogs-${agentId}`) || '[]');
        console.log('Server appears to be down, returning cached logs:', cachedLogs.length);
        return cachedLogs;
      }
      
      throw error;
    }
  });
},

async addTerminalLog(agentId, message, strategyId = null, externalId = null) {
  console.log(`tradingAgentApi.addTerminalLog: Adding log for agent ${agentId}`);
  return withRetry(async () => {
    try {
      const response = await api.post(`/agents/${agentId}/terminal-logs`, {
        message,
        strategy_id: strategyId,
        external_id: externalId
      });
      
      // Update local cache
      const cachedLogs = JSON.parse(localStorage.getItem(`terminalLogs-${agentId}`) || '[]');
      cachedLogs.unshift(response.data); // Add to beginning since we order by desc
      localStorage.setItem(`terminalLogs-${agentId}`, JSON.stringify(cachedLogs.slice(0, 100))); // Keep only 100
      
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.addTerminalLog ERROR:', error);
      
      // If server is down, cache locally
      if (error.isServerDown) {
        console.log('Server appears to be down, storing log locally');
        const cachedLogs = JSON.parse(localStorage.getItem(`terminalLogs-${agentId}`) || '[]');
        const newLog = {
          id: Date.now(),
          agent_id: agentId,
          strategy_id: strategyId,
          external_id: externalId,
          message,
          timestamp: new Date().toISOString()
        };
        cachedLogs.unshift(newLog);
        localStorage.setItem(`terminalLogs-${agentId}`, JSON.stringify(cachedLogs.slice(0, 100)));
        return newLog;
      }
      
      throw error;
    }
  });
},

async clearTerminalLogs(agentId) {
  console.log(`tradingAgentApi.clearTerminalLogs: Clearing logs for agent ${agentId}`);
  return withRetry(async () => {
    try {
      const response = await api.delete(`/agents/${agentId}/terminal-logs`);
      
      // Clear local cache
      localStorage.removeItem(`terminalLogs-${agentId}`);
      
      return response.data;
    } catch (error) {
      console.error('tradingAgentApi.clearTerminalLogs ERROR:', error);
      throw error;
    }
  });
},

// Cache terminal logs for offline use
cacheTerminalLogs(agentId, logs) {
  if (logs && logs.length > 0) {
    localStorage.setItem(`terminalLogs-${agentId}`, JSON.stringify(logs));
    console.log(`Cached ${logs.length} terminal logs for agent ${agentId}`);
  }
},
};

// Add a test method to the default export as well
api.testConnection = async () => {
  console.log('api.testConnection: Testing connection to backend API');
  return withRetry(async () => {
    try {
      const response = await api.get('/status');
      console.log('api.testConnection: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('api.testConnection ERROR:', error);
      
      if (error.isServerDown) {
        return { status: 'offline', message: 'Server is currently offline or restarting' };
      }
      
      throw error;
    }
  });


  
};




export default api;